<script setup lang="ts">
import type { BlockTextFragment } from '#graphql-operations'

interface BlockTextProps {
  data: BlockTextFragment
}
defineProps<BlockTextProps>()

const id = useId()
const expanded = ref(false)
</script>

<template>
  <Section padded class="blockText" :class="[{ blockTextFlow: data.displayMode !== 'slider' }, { blockTextSlider: data.displayMode === 'slider' }, { blockTextExpand: data.displayMode === 'expand' }, { blockTextDefault: data.displayMode === 'default' || !data.displayMode }]">
    <div v-if="data.displayMode === 'slider'" class="blockTextSlider c-$accent-color space-y-12">
      <div class="relative of-hidden">
        <BlossomCarousel :id="id" as="ul" class="carousel size-full">
          <li
            v-for="item in data.textArray"
            :key="item!.id"
            data-blossom-slide
            class="slide py-4"
          >
            <div v-if="item && item.text" class="container layout-grid">
              <div class="col-span-full lg:col-span-10 lg:col-start-2">
                <Text class="text-center" :html="item.text" />
              </div>
            </div>
          </li>
        </BlossomCarousel>
      </div>
      <div class="container flex gap-4 items-center justify-between">
        <BlossomPrev :for="id" :aria-label="$t('previous')">
          <ButtonIcon as="span" icon="chevron-left" />
        </BlossomPrev>
        <BlossomDots :for="id" class="flex gap-2 justify-center" />
        <BlossomNext :for="id" :aria-label="$t('next')">
          <ButtonIcon as="span" icon="chevron-right" />
        </BlossomNext>
      </div>
    </div>
    <div v-else-if="data.displayMode === 'expand'" class="blockTextExpand space-y-1lh">
      <div
        v-for="(item, index) in data.textArray"
        v-show="index === 0 || expanded"
        :key="item!.id"
      >
        <div v-if="item && item.text" class="container layout-grid">
          <Text class="col-span-full space-y-1lh lg:col-span-6 lg:col-start-4" :html="item.text" />
        </div>
      </div>
      <div v-if="!expanded && (data.textArray?.length ?? 0) > 1" class="container flex justify-center">
        <ButtonLink :title="$t('read_more')" @click="expanded = true" />
      </div>
    </div>
    <div v-else class="blockText space-y-6 lg:space-y-12">
      <div
        v-for="(item) in data.textArray"
        :key="item!.id"
      >
        <div v-if="item && item.text" class="container layout-grid">
          <Text
            class="richtext-article col-span-full space-y-1lh"
            :class="data.enlarge ? 'lg:col-span-8 lg:col-start-3' : 'lg:col-span-6 lg:col-start-4'"
            :style="{ color: data.color }"
            :html="item.text"
          />
        </div>
      </div>
    </div>
  </Section>
</template>

<style scoped>
.blockTextSlider .carousel {
  display: flex;
  align-items: center;
  overflow: auto hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
}

.blockTextSlider .slide {
  flex-shrink: 0;
  width: 100%;
  scroll-snap-align: start;
}

.blockTextSlider :deep(.blossom-dot) {
  background-color: var(--blossom-dot-color, currentColor);
  border-radius: 100%;

  inline-size: calc(var(--spacing) * 1.8);
  block-size: calc(var(--spacing) * 2);

  opacity: var(--blossom-dot-opacity, 0.35);

  &[aria-current='true'] {
    opacity: 1;
  }
}

.section.blockTextFlow:has(+ .section.blockTextFlow),
.blocks:has(+ .blocks > .section.blockTextFlow:first-child) > .section.blockTextFlow:last-child {
  margin-bottom: calc(var(--spacing) * 6);

  @screen lg {
    margin-bottom: calc(var(--spacing) * 12);
  }
}

.section.blockTextFlow + .section.blockTextFlow,
.blocks:has(> .section.blockTextFlow:last-child) + .blocks > .section.blockTextFlow:first-child {
  margin-top: calc(var(--spacing) * 6);

  @screen lg {
    margin-bottom: calc(var(--spacing) * 24);
  }
}

.blockText {
  & :deep(.typo-sans-1),
  & :deep(.typo-sans-2) {
    text-transform: uppercase;
  }
}
</style>
